import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import {
  CertificateDetail,
  SignatureDetail,
  SignatureStatus,
} from '../../models/dossier.model';
import { DossierService } from '../../services/dossier.service';

/** Which annex of Regulation (EU) 910/2014 sets the requirements for a given
 *  qualified certificate type. Annex I covers qualified certificates for
 *  electronic signatures (natural persons), Annex III those for electronic
 *  seals (legal persons), Annex IV website authentication. */
const ANNEX_BY_QC_TYPE: Record<string, string> = {
  QC_ESIGN: 'Annex I',
  QC_ESEAL: 'Annex III',
  QC_WEB: 'Annex IV',
};

const QC_TYPE_LABEL: Record<string, string> = {
  QC_ESIGN: 'Qualified Electronic Signature',
  QC_ESEAL: 'Qualified Electronic Seal',
  QC_WEB: 'Qualified Website Authentication Certificate',
};

/** RFC 5280 KeyUsage / ExtKeyUsage identifiers, as asn1crypto names them. */
const USAGE_LABEL: Record<string, string> = {
  digital_signature: 'Digital signature',
  non_repudiation: 'Non-repudiation (content commitment)',
  key_encipherment: 'Key encipherment',
  data_encipherment: 'Data encipherment',
  key_agreement: 'Key agreement',
  key_cert_sign: 'Certificate signing',
  crl_sign: 'CRL signing',
  encipher_only: 'Encipher only',
  decipher_only: 'Decipher only',
  server_auth: 'TLS server authentication',
  client_auth: 'TLS client authentication',
  code_signing: 'Code signing',
  email_protection: 'Email protection',
  time_stamping: 'Time stamping',
  ocsp_signing: 'OCSP response signing',
};

/**
 * Acrobat-equivalent signature panel: a summary of what was verified, the
 * certificate chain behind it, where the trust came from, and what is known
 * about revocation.
 *
 * Every statement it makes is backed by something dd_signatures actually
 * checked. Two distinctions the layout exists to preserve:
 *
 *   * "Not determined" is not "no". A missing qualification verdict means the
 *     EU trusted lists were unreachable or the chain did not reach one of
 *     their entries — never rendered as "not qualified".
 *   * "Last checked" is a snapshot. Verification runs once, at upload, and the
 *     result is stored; the panel reports when that happened rather than
 *     implying a live check on open.
 *
 * Layout adapted from Acrobat's Signature Properties dialog; the code is this
 * app's own (the lib/digsig reference is React and was not imported).
 */
@Component({
  selector: 'app-signature-panel',
  templateUrl: './signature-panel.component.html',
  styleUrls: ['./signature-panel.component.css'],
})
export class SignaturePanelComponent implements OnChanges {
  /** Title suffix, e.g. the filename these signatures belong to. */
  @Input() title: string | null = null;
  @Input() result: SignatureStatus | null = null;
  /** Set these to enable re-checking. Without them the panel is read-only,
   *  which is the right behaviour wherever the originals are not reachable. */
  @Input() dossierId: string | null = null;
  @Input() uploadId: string | null = null;

  @Output() close = new EventEmitter<void>();
  /** Fired after a successful re-check, so the badge that opened this panel
   *  can pick up the new verdict instead of showing the stale one. */
  @Output() rechecked = new EventEmitter<SignatureStatus>();

  tab: 'summary' | 'certificate' | 'trust' | 'revocation' = 'summary';
  /** Index into result.signatures — a PDF may carry several. */
  activeIndex = 0;
  /** Index into the displayed (root-first) chain. */
  selectedCertIndex = 0;

  rechecking = false;
  recheckError: string | null = null;

  constructor(private dossierService: DossierService) {}

  ngOnChanges(): void {
    this.tab = 'summary';
    this.activeIndex = 0;
    this.selectedCertIndex = 0;
    this.recheckError = null;
  }

  get canRecheck(): boolean {
    return !!this.dossierId && !!this.uploadId && !this.rechecking;
  }

  /** Re-verify against the trusted lists as they stand now. The stored record
   *  is a snapshot from upload time; this replaces it. */
  recheck(): void {
    if (!this.dossierId || !this.uploadId) return;
    this.rechecking = true;
    this.recheckError = null;
    this.dossierService.reverifyUploadSignature(this.dossierId, this.uploadId).subscribe({
      next: (data) => {
        const fresh = 'files' in data
          ? data.files.find((f) => f.filename === this.title) ?? data.files[0]
          : data;
        if (fresh) {
          this.result = fresh;
          this.activeIndex = 0;
          this.selectedCertIndex = 0;
          this.rechecked.emit(fresh);
        }
        this.rechecking = false;
      },
      error: (err) => {
        // 423 is this app's locked-dossier status: the originals are encrypted
        // and the DEK has not been unlocked in this session.
        this.recheckError = err?.status === 423
          ? 'Unlock the dossier first — the original file is encrypted at rest.'
          : 'Could not re-verify this file.';
        this.rechecking = false;
      },
    });
  }

  get signature(): SignatureDetail | null {
    return this.result?.signatures[this.activeIndex] ?? null;
  }

  selectSignature(i: number): void {
    this.activeIndex = i;
    this.selectedCertIndex = 0;
  }

  // -- Summary ------------------------------------------------------------

  /** Acrobat's headline verdict. */
  get headline(): string {
    const s = this.signature;
    if (!s) return 'No signature';
    if (s.status === 'INVALID') {
      return s.intact
        ? 'Signature is INVALID — it does not verify against the signer’s key'
        : 'Signature is INVALID — the document was altered after signing';
    }
    if (s.status === 'WARNING') {
      return 'Signature validity is UNKNOWN — the signer’s identity could not be verified';
    }
    return 'Signature is VALID, signed by the certificate holder below';
  }

  /** True when the trust decision rests on the EU Trusted Lists rather than
   *  on a locally configured anchor. Qualification is only ever assessed
   *  against trusted-list entries, so its presence is the reliable signal. */
  get fromTrustedLists(): boolean {
    return !!this.signature?.qualification;
  }

  get trustedListsCheckedAt(): string | null {
    return this.result?.trustedLists?.last_checked ?? null;
  }

  /** Statement about where the signing time comes from. B-B means nothing but
   *  the signer's own machine asserts it. */
  get signingTimeStatement(): string {
    const s = this.signature;
    if (!s) return '';
    if (s.adesLevel === 'B-T' && s.timestampTime) {
      return 'Signing time is vouched for by a trusted timestamp (AdES level B-T).';
    }
    return 'Signing time is from the clock on the signer’s computer — no timestamp authority vouches for it (AdES level B-B).';
  }

  /** Acrobat's LTV line. dd never claims LTV: establishing it means proving
   *  the document carries the revocation material needed to validate long
   *  after signing, which dd_signatures does not inspect. */
  get ltvStatement(): string {
    const expiry = this.signature?.certificate?.validTo;
    return expiry
      ? 'Signature is not LTV enabled and will expire after ' + this.formatDate(expiry) + '.'
      : 'Signature is not LTV enabled.';
  }

  get integrityStatement(): string {
    const s = this.signature;
    if (!s) return '';
    if (!s.intact) return 'The document has been altered since this signature was applied.';
    if (s.coverage === 'ENTIRE_FILE') {
      return 'The document has not been modified since this signature was applied.';
    }
    if (s.coverage === 'ENTIRE_REVISION') {
      return 'The revision this signature covers is unmodified; a later revision was appended afterwards (normal for any signature that is not the most recent).';
    }
    return 'The signed bytes are unmodified.';
  }

  get identityStatement(): string {
    const s = this.signature;
    if (!s) return '';
    if (!s.trusted) {
      return 'The signer’s identity is unknown — the certificate does not chain to the EU Trusted Lists or to a configured trust anchor.';
    }
    return this.fromTrustedLists
      ? 'The signer’s identity is valid — the certificate chains to a trust service published in the EU Trusted Lists.'
      : 'The signer’s identity is valid — the certificate chains to a configured trust anchor.';
  }

  /** Non-null only when a qualification verdict was actually reached. */
  get qualificationStatement(): string | null {
    const q = this.signature?.qualification;
    if (!q) return null;
    if (!q.qualified) {
      return 'This certificate is published in the EU Trusted Lists but is NOT qualified under Regulation (EU) 910/2014.';
    }
    const label = QC_TYPE_LABEL[q.qcType ?? ''] ?? 'Qualified certificate';
    const qscd = q.qscd
      ? ' The private key is declared to reside in a qualified signature creation device (QSCD).'
      : '';
    return `This is a ${label} under Regulation (EU) 910/2014.${qscd}`;
  }

  // -- Certificate chain --------------------------------------------------

  /** Chain root first, the way Acrobat draws the tree. The API sends it
   *  leaf first. Falls back to the signer certificate alone when no path was
   *  built, so the panel always has something to show. */
  get chain(): CertificateDetail[] {
    const s = this.signature;
    if (!s) return [];
    if (s.chain.length) return [...s.chain].reverse();
    return s.certificate ? [s.certificate] : [];
  }

  get selectedCert(): CertificateDetail | null {
    return this.chain[this.selectedCertIndex] ?? null;
  }

  /** True for the last entry of the displayed chain — the signer itself. */
  isLeaf(i: number): boolean {
    return i === this.chain.length - 1;
  }

  /** The Annex III-style note, shown only on the signer certificate and only
   *  when a qualification verdict was actually reached. */
  get annexNote(): string | null {
    const q = this.signature?.qualification;
    if (!q || !q.qualified) return null;
    if (!this.isLeaf(this.selectedCertIndex)) return null;
    const annex = ANNEX_BY_QC_TYPE[q.qcType ?? ''];
    return annex
      ? `This certificate is Qualified according to EU Regulation 910/2014 ${annex}.`
      : 'This certificate is Qualified according to EU Regulation 910/2014.';
  }

  usageLabel(usage: string): string {
    return USAGE_LABEL[usage] ?? usage;
  }

  get intendedUsage(): string[] {
    const c = this.selectedCert;
    if (!c) return [];
    return [...c.keyUsage, ...c.extendedKeyUsage].map((u) => this.usageLabel(u));
  }

  /** Save the selected certificate as a DER .cer file, built from the base64
   *  the API already sent — no second round trip. */
  exportCertificate(): void {
    const cert = this.selectedCert;
    if (!cert?.derB64) return;
    const binary = atob(cert.derB64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const name = (cert.subject.commonName || 'certificate').replace(/[^\w.-]+/g, '_');
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pkix-cert' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${name}.cer`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // -- Trust --------------------------------------------------------------

  /** What the signer's certificate is trusted for, derived from the KeyUsage
   *  bits it asserts. Acrobat's other trust settings (dynamic content,
   *  privileged JavaScript, privileged system operations) are Acrobat runtime
   *  permissions with no counterpart here, so they are not shown. */
  get trustSettings(): { label: string; granted: boolean }[] {
    const cert = this.signature?.certificate;
    const usage = cert?.keyUsage ?? [];
    const trusted = !!this.signature?.trusted;
    return [
      {
        label: 'Sign documents or data',
        granted:
          trusted &&
          (usage.includes('digital_signature') || usage.includes('non_repudiation')),
      },
      {
        label: 'Certify documents',
        granted: trusted && usage.includes('key_cert_sign'),
      },
    ];
  }

  get trustSourceStatement(): string {
    const lists = this.result?.trustedLists;
    if (this.fromTrustedLists && lists?.last_checked) {
      return (
        'Source of Trust obtained from the EU Trusted Lists, last checked on ' +
        this.formatDate(lists.last_checked) +
        '.'
      );
    }
    if (this.signature?.trusted) {
      return 'Source of Trust obtained from a trust anchor configured for this deployment.';
    }
    if (lists && !lists.ready) {
      return 'The EU Trusted Lists were not available when this document was verified, so no source of trust could be established.';
    }
    return 'No source of trust could be established for this certificate.';
  }

  // -- Helpers ------------------------------------------------------------

  /** Browser locale, per this app's convention — never the language picker. */
  formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
  }

  coverageLabel(coverage: string | null): string {
    switch (coverage) {
      case 'ENTIRE_FILE':
        return 'Covers the entire file';
      case 'ENTIRE_REVISION':
        return 'Covers this signature’s revision (a later revision was appended)';
      default:
        return coverage || '—';
    }
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'VALID':
        return 'Valid';
      case 'WARNING':
        return 'Unknown';
      case 'INVALID':
        return 'Invalid';
      default:
        return 'No signature';
    }
  }
}
